import { inject, Injectable } from '@angular/core';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, forkJoin, from, map, of, switchMap } from 'rxjs';

import { DataRepository } from '@core/data/data-repository';
import { OnboardingApiActions, OnboardingPageActions } from './onboarding.actions';

/** Loads onboarding documents and the new-hire pipeline together. */
@Injectable()
export class OnboardingEffects {
  private readonly actions$ = inject(Actions);
  private readonly repo = inject(DataRepository);

  // On page-enter, load doc templates and new-hires in parallel (forkJoin) and
  // dispatch loadSuccess with both, or loadFailure on any error.
  readonly load$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OnboardingPageActions.entered),
      switchMap(() =>
        forkJoin({
          docs: from(this.repo.listOnboardingDocs()),
          newHires: from(this.repo.listNewHires()),
        }).pipe(
          map((r) => OnboardingApiActions.loadSuccess(r)),
          catchError((err: unknown) =>
            of(OnboardingApiActions.loadFailure({ error: toMessage(err) })),
          ),
        ),
      ),
    ),
  );
}

/** Normalize any thrown value to a display string for loadFailure. */
function toMessage(err: unknown): string {
  return err instanceof Error ? err.message : 'Unknown error';
}
